import { CustomizerEngine, ImageLayer, SegmentGraphemes, SeparatorCutoutConfig, TextCutoutConfig } from 'smg/customizer';
import { Debounce } from 'smg/util';
import { COIN_DPI, CoinTextLayer, SeparatorType } from './coin-text-layer';
import { CoinTextRing } from './coin-text-ring';
import { DEFAULT_FONT_FAMILY, FontHasFace, FONTS, RegisterDropdownFaces, ResolvedFontFace, ResolveFontFace, ResolveFontFamily } from '../product-customizer/fonts';

export const DEFAULT_TOP_TEXT = 'TOP TEXT';
export const DEFAULT_BOTTOM_TEXT = 'BOTTOM TEXT';

// Ring dimensions for shrink-to-fit bounds and the preview's arch-text click target
export const RING_INNER = 325;
export const RING_OUTER = 408;

// Available font sizes in pt (must match dropdown options)
const AVAILABLE_FONT_SIZES_PT = [4, 5, 6, 7, 8, 9, 10, 11];
const DEFAULT_FONT_SIZE_PT = 11;

function ptToPx(pt: number): number {
    return Math.round(pt * (COIN_DPI / 72));
}

// Separator positioning constants
const NEAR_WRAP_THRESHOLD = 98; // Hide separators if text fills more than this % of circumference
const OVERLAP_MARGIN = 3; // Minimum gap (%) on each side of separator to fit

// Special markers for single-text separator placement
const USE_TOP_LAYER_CENTER = -1;
const USE_BOTTOM_LAYER_CENTER = -2;

// Font-specific underline adjustments
// Oswald: taller descenders need extra clearance, measured length undershoots visual extent
const OSWALD_UNDERLINE_OFFSET_RATIO = 0.75;
const OSWALD_MAX_UNDERLINE_OFFSET = 38;
const OSWALD_UNDERLINE_WIDTH_RATIO = 1.07;

// Roboto/Roboto Condensed: measured length overshoots visual extent
const ROBOTO_UNDERLINE_WIDTH_RATIO = 0.97;

// Mask dimensions for upload area
// Canvas is 969x969, center at 484.5
const CANVAS_SIZE = 969;
const CANVAS_CENTER = CANVAS_SIZE / 2;
// With text ring: smaller circle that fits inside the ring (radius ~322)
const MASK_RADIUS_WITH_RING = 322;
// Without text ring: larger circle extending to where ring outer edge would be (radius ~408)
const MASK_RADIUS_NO_RING = 408;

/** Serialized text customizer state for persistence (per side) */
export interface TextSideState {
    topText: string;
    bottomText: string;
    fontFamily: string;
    topFontSize: number;
    bottomFontSize: number;
    /** @deprecated Use topFontSize/bottomFontSize instead. Kept for backwards compat with old saved states. */
    fontSize?: number;
    topFontColor: string;
    bottomFontColor: string;
    /** @deprecated Use topFontColor/bottomFontColor instead. Kept for backwards compat with old saved states. */
    fontColor?: string;
    fontStyles: { bold: boolean; italic: boolean; underline: boolean; allCaps: boolean };
    separator: { type: SeparatorType; customChars: string[]; color: string | null; sizePt: number };
    ring: { wantsRing: boolean; useColor: boolean; color: string; translucent: boolean };
    isMetalMode: boolean;
}

/** Full text customizer state for persistence */
export interface TextState {
    front: TextSideState;
    back: TextSideState;
}

export class TextCustomizerUI {
    #frontEngine: CustomizerEngine;
    #backEngine: CustomizerEngine;
    #currentSide: 'front' | 'back' = 'front';

    // DOM elements
    #topTextInput: HTMLInputElement | null = null;
    #bottomTextInput: HTMLInputElement | null = null;
    #fontFamilyDropdown: HTMLElement | null = null;
    #fontFamilyBtn: HTMLButtonElement | null = null;
    #fontSizeDropdown: HTMLElement | null = null;
    #fontSizeBtn: HTMLButtonElement | null = null;
    #textColorPicker: HTMLInputElement | null = null;
    #textColorHexDisplay: HTMLElement | null = null;
    #boldBtn: HTMLButtonElement | null = null;
    #italicBtn: HTMLButtonElement | null = null;
    #underlineBtn: HTMLButtonElement | null = null;
    #capsBtn: HTMLButtonElement | null = null;

    // Font style state (per side)
    #fontStyles: Record<'front' | 'back', { bold: boolean; italic: boolean; underline: boolean; allCaps: boolean }> = {
        front: { bold: true, italic: false, underline: false, allCaps: true },
        back: { bold: true, italic: false, underline: false, allCaps: true },
    };

    // Separator state (per side)
    #separatorSettings: Record<'front' | 'back', { type: SeparatorType; customChars: string[]; color: string | null; sizePt: number }> = {
        front: { type: 'none', customChars: [], color: null, sizePt: DEFAULT_FONT_SIZE_PT },
        back: { type: 'none', customChars: [], color: null, sizePt: DEFAULT_FONT_SIZE_PT },
    };

    // Toggles custom separator between single/split mode based on text state
    #updateCustomSeparatorMode: (() => void) | null = null;

    // Invalidates in-flight face fetches when a newer apply/restore starts (per side)
    #faceSeq: Record<'front' | 'back', number> = { front: 0, back: 0 };

    // Text ring instances (per side)
    #frontRing: CoinTextRing;
    #backRing: CoinTextRing;

    // Track if Metal mode is active for text color (per side)
    #isMetalMode: Record<'front' | 'back', boolean> = {
        front: true, // Default to Metal (transparent text)
        back: true,
    };

    // Per-layer text colors (independent top/bottom)
    #textColors: Record<'front' | 'back', { top: string; bottom: string }> = {
        front: { top: '#000000', bottom: '#000000' },
        back: { top: '#000000', bottom: '#000000' },
    };

    // Track ring settings per side (useColor: false = Metal mode, true = Color mode)
    #ringSettings: Record<'front' | 'back', { wantsRing: boolean; useColor: boolean; color: string; translucent: boolean }> = {
        front: { wantsRing: true, useColor: true, color: '#0033A0', translucent: false },
        back: { wantsRing: true, useColor: true, color: '#0033A0', translucent: false },
    };

    #wantsTextRingToggle: HTMLInputElement | null = null;
    #textRingToggle: HTMLInputElement | null = null;
    #textRingColorSwatch: HTMLInputElement | null = null;
    #textRingColorPicker: HTMLElement | null = null;
    // All text control containers that should be hidden when "no text ring"
    #textControlContainers: HTMLElement[] = [];

    // Debounced shrink-to-fit for text input (500ms delay)
    #debouncedShrinkToFit: () => void;

    constructor(frontEngine: CustomizerEngine, backEngine: CustomizerEngine) {
        this.#frontEngine = frontEngine;
        this.#backEngine = backEngine;

        this.#debouncedShrinkToFit = Debounce(() => {
            void this.#runShrinkToFit();
        }, 500, false);

        // Initialize text rings (enabled by default with blue color)
        this.#frontRing = new CoinTextRing(frontEngine, {
            enabled: true,
            color: '#0033A0',
        });
        this.#backRing = new CoinTextRing(backEngine, {
            enabled: true,
            color: '#0033A0',
        });

        this.#initializeUI();
    }

    get #currentEngine(): CustomizerEngine {
        return this.#currentSide === 'front' ? this.#frontEngine : this.#backEngine;
    }

    /** Gets both text layers for current side, returns [topLayer, bottomLayer] */
    #getTextLayers(): [CoinTextLayer | undefined, CoinTextLayer | undefined] {
        const top = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
        const bottom = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;
        return [top, bottom];
    }

    /** Get the current side's ring */
    get #currentRing(): CoinTextRing {
        return this.#currentSide === 'front' ? this.#frontRing : this.#backRing;
    }

    /**
     * Update ring text cutouts based on current text and font settings.
     * Called when text, font, or Metal mode changes.
     * Uses async text measurement to ensure fonts are loaded before measuring.
     */
    async #updateRingTextCutouts(): Promise<void> {
        const isMetalMode = this.#isMetalMode[this.#currentSide];
        const ring = this.#currentRing;

        if (!isMetalMode) {
            // Disable cutouts with empty array (single atomic update)
            ring.updateTextCutouts(false, []);
            return;
        }

        const [topLayer, bottomLayer] = this.#getTextLayers();
        const allCaps = this.#fontStyles[this.#currentSide].allCaps;
        const topText = allCaps ? (topLayer?.getText() || '').toUpperCase() : topLayer?.getText() || '';
        const bottomText = allCaps ? (bottomLayer?.getText() || '').toUpperCase() : bottomLayer?.getText() || '';

        const styles = this.#fontStyles[this.#currentSide];
        const fontWeight = styles.bold ? 'bold' : 'normal';
        const fontStyle = styles.italic ? 'italic' : 'normal';
        const textDecoration = styles.underline ? 'underline' : 'none';

        // Text radius matches the coin text layer radius (365)
        const textRadius = 365;

        // Build cutout configs
        const cutouts: TextCutoutConfig[] = [];

        if (topText && topLayer) {
            const topFont = topLayer.getFont();
            const fontFamily = topFont.family || DEFAULT_FONT_FAMILY;
            const fontUrl = topFont.fontUrl;
            const topFontSizePx = ptToPx(topFont.size ?? 11);
            const { underlineOffsetRatio, maxUnderlineOffset, underlineWidthRatio } = this.#getUnderlineSettings(fontFamily);
            const baselineOffset = (topFont.baselineOffset ?? 0) * topFontSizePx;

            cutouts.push({
                text: topText,
                position: 'top',
                fontFamily,
                fontSize: topFontSizePx,
                fontWeight: (topFont.weight ?? fontWeight) as 'normal' | 'bold',
                fontStyle,
                textDecoration,
                textLengthPercent: await topLayer.getTextLengthPercentAsync(),
                radius: textRadius,
                fontUrl,
                letterSpacing: topFont.letterSpacing,
                baselineOffset,
                emojiOffset: topFont.emojiOffset,
                underlineOffsetRatio,
                maxUnderlineOffset,
                underlineWidthRatio,
            });
        }

        if (bottomText && bottomLayer) {
            const bottomFont = bottomLayer.getFont();
            const fontFamily = bottomFont.family || DEFAULT_FONT_FAMILY;
            const fontUrl = bottomFont.fontUrl;
            const bottomFontSizePx = ptToPx(bottomFont.size ?? 11);
            const { underlineOffsetRatio, maxUnderlineOffset, underlineWidthRatio } = this.#getUnderlineSettings(fontFamily);
            const baselineOffset = (bottomFont.baselineOffset ?? 0) * bottomFontSizePx;

            cutouts.push({
                text: bottomText,
                position: 'bottom',
                fontFamily,
                fontSize: bottomFontSizePx,
                fontWeight: (bottomFont.weight ?? fontWeight) as 'normal' | 'bold',
                fontStyle,
                textDecoration,
                textLengthPercent: await bottomLayer.getTextLengthPercentAsync(),
                radius: textRadius,
                fontUrl,
                letterSpacing: bottomFont.letterSpacing,
                baselineOffset,
                emojiOffset: bottomFont.emojiOffset,
                underlineOffsetRatio,
                maxUnderlineOffset,
                underlineWidthRatio,
            });
        }

        // Gather separator data from both layers
        // Note: separators never get italic or underline (fontStyle/textDecoration omitted)
        const separators: SeparatorCutoutConfig[] = [];

        for (const layer of [topLayer, bottomLayer]) {
            if (layer) {
                const font = layer.getFont();
                const sepSizePx = ptToPx(layer.getSeparatorSize());
                const layerSeparators = layer.getSeparatorCutoutData();
                for (const sep of layerSeparators) {
                    separators.push({
                        ...sep,
                        fontFamily: font.family || DEFAULT_FONT_FAMILY,
                        fontSize: sepSizePx,
                        fontWeight: (font.weight ?? 'bold') as 'normal' | 'bold',
                        fontUrl: font.fontUrl,
                    });
                }
            }
        }

        ring.updateTextCutouts(true, cutouts, separators);
    }

    /** Applies a function to both text layers on current side */
    #applyToBothLayers(fn: (layer: CoinTextLayer) => void): void {
        const [top, bottom] = this.#getTextLayers();
        if (top) {
            fn(top);
        }
        if (bottom) {
            fn(bottom);
        }
    }

    /** Applies an async function to both text layers on current side and waits for both */
    async #applyToBothLayersAsync(fn: (layer: CoinTextLayer) => Promise<void>): Promise<void> {
        const [top, bottom] = this.#getTextLayers();
        const promises: Promise<void>[] = [];
        if (top) {
            promises.push(fn(top));
        }
        if (bottom) {
            promises.push(fn(bottom));
        }
        await Promise.all(promises);
    }

    #initializeUI(): void {
        this.#setupTextInputs();
        this.#setupFontControls();
        this.#setupStyleToggles();
        this.#setupColorControls();
        this.#setupSeparatorControls();
        this.#setupSideChangeListener();
        this.#setupUndoRedoSync();
        this.#setDefaultText();
        this.#updateStyleToggleUI();
        this.#applyInitialRingState();
    }

    /** Both sides start on the default self-hosted face (page layers only carry the
     * family name). The page calls this only when no saved design is being restored,
     * so a slow default-face fetch can never clobber a restored font. */
    async applyInitialFaces(): Promise<void> {
        await Promise.all([
            this.#applyFaceForSide('front', DEFAULT_FONT_FAMILY),
            this.#applyFaceForSide('back', DEFAULT_FONT_FAMILY),
        ]);
        this.#updateStyleToggleUI();
        void this.#updateRingTextCutouts();
    }

    #applyInitialRingState(): void {
        for (const [engine, side] of [[this.#frontEngine, 'front'], [this.#backEngine, 'back']] as const) {
            const wantsRing = this.#ringSettings[side].wantsRing;

            const topLayer = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
            const bottomLayer = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;
            if (topLayer) {
                topLayer.setVisible(wantsRing);
            }
            if (bottomLayer) {
                bottomLayer.setVisible(wantsRing);
            }

            const radius = wantsRing ? MASK_RADIUS_WITH_RING : MASK_RADIUS_NO_RING;
            const maskUrl = this.#generateCircleMask(radius);
            const diameter = radius * 2;

            engine.defineImageZone({
                id: 'coin-center',
                maskUrl,
                bounds: {
                    x: CANVAS_CENTER - radius,
                    y: CANVAS_CENTER - radius,
                    width: diameter,
                    height: diameter,
                },
            });
        }
    }

    #setupUndoRedoSync(): void {
        const syncInputs = () => {
            const [topLayer, bottomLayer] = this.#getTextLayers();
            if (this.#topTextInput && topLayer) {
                this.#topTextInput.value = topLayer.getText();
            }
            if (this.#bottomTextInput && bottomLayer) {
                this.#bottomTextInput.value = bottomLayer.getText();
            }
            void this.#runShrinkToFit();
        };

        this.#frontEngine.on('actionUndone', syncInputs);
        this.#frontEngine.on('actionRedone', syncInputs);
        this.#backEngine.on('actionUndone', syncInputs);
        this.#backEngine.on('actionRedone', syncInputs);
    }

    #setDefaultText(): void {
        // Only sync input fields - don't set text on layers here.
        // Text will be set either by:
        // 1. User typing (which calls setText)
        // 2. restore() setting saved text via restoreState()
        // 3. setDefaultTextOnLayers() if no saved state exists
        // This avoids race conditions with restore's async updateCanvas()
        if (this.#topTextInput) {
            this.#topTextInput.value = DEFAULT_TOP_TEXT;
        }
        if (this.#bottomTextInput) {
            this.#bottomTextInput.value = DEFAULT_BOTTOM_TEXT;
        }
    }

    /**
     * Update ring cutouts and separators for both sides.
     * This is called during restore and default text setup to ensure both sides are consistent.
     * IMPORTANT: #updateRingTextCutouts reads text from UI inputs, so this method
     * temporarily switches sides and updates inputs before calling it.
     */
    #updateBothSidesCutoutsAndSeparators(): void {
        const originalSide = this.#currentSide;

        this.#currentSide = 'front';
        const frontTopLayer = this.#frontEngine.getLayer('front-top-text') as CoinTextLayer | undefined;
        const frontBottomLayer = this.#frontEngine.getLayer('front-bottom-text') as CoinTextLayer | undefined;
        if (this.#topTextInput) {
            this.#topTextInput.value = frontTopLayer?.getText() ?? '';
        }
        if (this.#bottomTextInput) {
            this.#bottomTextInput.value = frontBottomLayer?.getText() ?? '';
        }
        this.#updateSeparators();
        if (this.#isMetalMode.front) {
            void this.#updateRingTextCutouts();
        }

        this.#currentSide = 'back';
        const backTopLayer = this.#backEngine.getLayer('back-top-text') as CoinTextLayer | undefined;
        const backBottomLayer = this.#backEngine.getLayer('back-bottom-text') as CoinTextLayer | undefined;
        if (this.#topTextInput) {
            this.#topTextInput.value = backTopLayer?.getText() ?? '';
        }
        if (this.#bottomTextInput) {
            this.#bottomTextInput.value = backBottomLayer?.getText() ?? '';
        }
        this.#updateSeparators();
        if (this.#isMetalMode.back) {
            void this.#updateRingTextCutouts();
        }

        this.#currentSide = originalSide;
        const currentTopLayer = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
        const currentBottomLayer = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;
        if (this.#topTextInput) {
            this.#topTextInput.value = currentTopLayer?.getText() ?? '';
        }
        if (this.#bottomTextInput) {
            this.#bottomTextInput.value = currentBottomLayer?.getText() ?? '';
        }

        // Sync custom separator single/split mode based on current text state
        this.#updateCustomSeparatorMode?.();
    }

    /**
     * Set default placeholder text on layers. Called only when no saved state exists.
     * Also applies the default metal mode styling (transparent text with ring cutouts).
     */
    async setDefaultTextOnLayers(): Promise<void> {
        const promises: Promise<void>[] = [];
        for (const [engine, side] of [[this.#frontEngine, 'front'], [this.#backEngine, 'back']] as const) {
            const top = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
            const bottom = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;
            const styles = this.#fontStyles[side];
            const font = {
                weight: styles.bold ? 'bold' : 'normal',
                style: styles.italic ? 'italic' : 'normal',
                textDecoration: styles.underline ? 'underline' : 'none',
                casing: styles.allCaps ? 'uppercase' as const : 'normal' as const,
            };

            top?.setText(DEFAULT_TOP_TEXT);
            bottom?.setText(DEFAULT_BOTTOM_TEXT);
            if (top) {
                promises.push(top.setFont(font));
            }
            if (bottom) {
                promises.push(bottom.setFont(font));
            }

            // Apply default metal mode text color (transparent)
            if (this.#isMetalMode[side]) {
                top?.setColor('none');
                bottom?.setColor('none');
            }
        }
        await Promise.all(promises);

        this.#updateBothSidesCutoutsAndSeparators();
        void this.#adjustLetterSpacingBothSides();
    }

    async #adjustLetterSpacingBothSides(): Promise<boolean> {
        const didAdjust = await this.#applyPerLayerLetterSpacing(this.#getAllTextLayers());
        // Metal-mode text lives in the ring cutout, so rebuild cutouts too.
        this.#updateBothSidesCutoutsAndSeparators();
        return didAdjust;
    }

    #getAllTextLayers(): CoinTextLayer[] {
        const layers: CoinTextLayer[] = [];
        for (const engine of [this.#frontEngine, this.#backEngine]) {
            const sidePrefix = engine === this.#frontEngine ? 'front' : 'back';
            const top = engine.getLayer(`${sidePrefix}-top-text`) as CoinTextLayer | undefined;
            const bottom = engine.getLayer(`${sidePrefix}-bottom-text`) as CoinTextLayer | undefined;
            if (top) {
                layers.push(top);
            }
            if (bottom) {
                layers.push(bottom);
            }
        }
        return layers;
    }

    /**
     * Compute and apply each layer's own required spacing independently. A
     * script font on the back shouldn't drag all four arches wider — per-layer
     * respects each arch's actual content and font.
     */
    async #applyPerLayerLetterSpacing(layers: CoinTextLayer[]): Promise<boolean> {
        let didAdjust = false;
        await Promise.all(layers.map(async layer => {
            const needed = await layer.computeRequiredLetterSpacing();
            if (needed === null) {
                return;
            }
            const current = layer.getFont().letterSpacing ?? 0;
            if (Math.abs(needed - current) < 0.1) {
                return;
            }
            await layer.setFont({ letterSpacing: needed });
            didAdjust = true;
        }));
        return didAdjust;
    }

    #setupTextInputs(): void {
        this.#topTextInput = document.getElementById('top-text-input') as HTMLInputElement;
        this.#bottomTextInput = document.getElementById('bottom-text-input') as HTMLInputElement;

        if (!this.#topTextInput || !this.#bottomTextInput) {
            console.error('Text inputs not found');
            return;
        }

        // Text wraps naturally along the curved path — no character limit needed
        this.#topTextInput.addEventListener('input', () => {
            const text = this.#topTextInput?.value ?? '';
            const layer = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
            if (layer) {
                layer.setCasing(this.#fontStyles[this.#currentSide].allCaps ? 'uppercase' : 'normal');
                layer.setText(text);
                this.#updateCustomSeparatorMode?.();
                this.#updateSeparators();
                void this.#updateRingTextCutouts();
                this.#debouncedShrinkToFit();
            }
        });

        this.#bottomTextInput.addEventListener('input', () => {
            const text = this.#bottomTextInput?.value ?? '';
            const layer = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;
            if (layer) {
                layer.setCasing(this.#fontStyles[this.#currentSide].allCaps ? 'uppercase' : 'normal');
                layer.setText(text);
                this.#updateCustomSeparatorMode?.();
                this.#updateSeparators();
                void this.#updateRingTextCutouts();
                this.#debouncedShrinkToFit();
            }
        });
    }

    #setupFontControls(): void {
        const fontSection = document.querySelector('.coin-text-question-container:has(.dropdown)');
        if (!fontSection) {
            console.error('Font section not found');
            return;
        }

        const dropdowns = fontSection.querySelectorAll('.dropdown');
        if (dropdowns.length < 2) {
            console.error('Font dropdowns not found');
            return;
        }

        // First dropdown is font family
        this.#fontFamilyDropdown = dropdowns[0] as HTMLElement;
        this.#fontFamilyBtn = this.#fontFamilyDropdown.querySelector('.dropdown-toggle') as HTMLButtonElement;
        const fontFamilyMenu = this.#fontFamilyDropdown.querySelector('.dropdown-menu') as HTMLElement;

        // Second dropdown is font size
        this.#fontSizeDropdown = dropdowns[1] as HTMLElement;
        this.#fontSizeBtn = this.#fontSizeDropdown.querySelector('.dropdown-toggle') as HTMLButtonElement;
        const fontSizeMenu = this.#fontSizeDropdown.querySelector('.dropdown-menu') as HTMLElement;

        // Register each family's regular face so dropdown items render in their own font
        RegisterDropdownFaces();

        if (fontFamilyMenu) {
            fontFamilyMenu.innerHTML = '';
            for (const font of FONTS) {
                fontFamilyMenu.appendChild(this.#createFontMenuItem(font.name));
            }
        }

        // Populate font size dropdown (values in pt, converted to px when applied)
        // 11pt ≈ 74px is the max (similar to previous 72px max)
        if (fontSizeMenu) {
            fontSizeMenu.innerHTML = '';
            for (const sizePt of AVAILABLE_FONT_SIZES_PT) {
                const li = document.createElement('li');
                const a = document.createElement('a');
                a.className = 'dropdown-item';
                a.href = '#';
                a.textContent = String(sizePt);
                a.addEventListener('click', e => {
                    e.preventDefault();
                    void this.#applyFontSizePt(sizePt);
                });
                li.appendChild(a);
                fontSizeMenu.appendChild(li);
            }
        }

        if (this.#fontFamilyBtn) {
            this.#fontFamilyBtn.textContent = DEFAULT_FONT_FAMILY;
        }

        if (this.#fontSizeBtn) {
            this.#fontSizeBtn.textContent = '11';
        }
    }

    #createFontMenuItem(font: string): HTMLLIElement {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'dropdown-item';
        a.href = '#';
        a.textContent = font;
        a.style.fontFamily = font;
        a.addEventListener('click', e => {
            e.preventDefault();
            void this.#applyFontFamily(font);
        });
        li.appendChild(a);
        return li;
    }

    async #applyFontFamily(fontFamily: string): Promise<void> {
        // Label updates only after the face actually applies — on a failed or
        // superseded fetch the menu must keep telling the truth
        const applied = await this.#applyFaceForSide(this.#currentSide, fontFamily);
        if (!applied) {
            return;
        }
        if (this.#fontFamilyBtn) {
            this.#fontFamilyBtn.textContent = ResolveFontFamily(fontFamily);
        }
        this.#updateStyleToggleUI();

        // Update cutouts with new font, then shrink-to-fit if needed
        void this.#updateRingTextCutouts();
        void this.#runShrinkToFit();
    }

    /** Load the real face for a family + this side's styles and apply it to both ring
     * layers (#8141). Returns false when the fetch failed (layers keep their current
     * face) or a newer apply/restore superseded this one while the file loaded. */
    async #applyFaceForSide(side: 'front' | 'back', familyName: string): Promise<boolean> {
        const seq = ++this.#faceSeq[side];
        const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
        const topLayer = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
        const bottomLayer = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;

        const styles = this.#fontStyles[side];
        let face: ResolvedFontFace;
        try {
            face = await ResolveFontFace(familyName, styles.bold, styles.italic);
        } catch (err) {
            // Keep the current face rather than falling back to a device font
            // the print files couldn't match
            console.error('failed to load font face', err);
            return false;
        }
        if (seq !== this.#faceSeq[side]) {
            return false;
        }

        // Styles the family has no real face for turn off instead of synthesizing
        styles.bold = face.bold;
        styles.italic = face.italic;

        const fontConfig = {
            family: face.family,
            weight: face.weight,
            style: face.style,
            fontUrl: face.fontUrl,
            exactFontFace: true,
            textDecoration: styles.underline ? 'underline' : 'none',
            casing: (styles.allCaps ? 'uppercase' : 'normal') as 'uppercase' | 'normal',
            ...this.#getUnderlineSettings(face.family),
        };
        await Promise.all([topLayer?.setFont(fontConfig), bottomLayer?.setFont(fontConfig)]);
        return true;
    }

    /**
     * Shrinks text to fit within ring bounds using pixel-based measurement.
     * Uses shrinkToFitAsync() to accurately measure rendered text including descenders and emojis.
     * Top and bottom text shrink independently because users can size them separately.
     */
    async #runShrinkToFit(): Promise<void> {
        const [topLayer, bottomLayer] = this.#getTextLayers();
        if (!topLayer && !bottomLayer) {
            return;
        }

        // Wait a frame for any pending canvas updates (setFont triggers async updateCanvas)
        await new Promise(resolve => requestAnimationFrame(resolve));

        const minFontSizePx = ptToPx(AVAILABLE_FONT_SIZES_PT[0]);
        let didShrinkAny = false;

        // Shrink each layer independently
        for (const layer of [topLayer, bottomLayer]) {
            if (!layer) {
                continue;
            }
            const result = await layer.shrinkToFitAsync(RING_INNER, RING_OUTER, minFontSizePx);
            if (result?.didShrink && result.fontSize !== undefined) {
                let finalPt = AVAILABLE_FONT_SIZES_PT[0];
                for (const sizePt of AVAILABLE_FONT_SIZES_PT) {
                    if (ptToPx(sizePt) <= result.fontSize) {
                        finalPt = sizePt;
                    } else {
                        break;
                    }
                }
                await layer.setFont({ size: finalPt });
                didShrinkAny = true;
            }
        }

        await new Promise(resolve => requestAnimationFrame(resolve));

        // Re-run letter-spacing now that the font size has settled.
        const didAdjustSpacing = await this.#applyPerLayerLetterSpacing(this.#getAllTextLayers());

        await new Promise(resolve => requestAnimationFrame(resolve));
        this.#updateSeparators(); // recalc after font size settled

        // Shrink separators independently if they overflow
        for (const layer of [topLayer, bottomLayer]) {
            if (!layer) {
                continue;
            }
            const sepResult = await layer.shrinkSeparatorsToFit(RING_INNER, RING_OUTER);
            if (sepResult.didShrink) {
                layer.setSeparatorSize(sepResult.sizePt);
                this.#separatorSettings[this.#currentSide].sizePt = sepResult.sizePt;
                didShrinkAny = true;
            }

            // At minimum size and still overflowing — trim each separator
            // independently until it fits
            if (sepResult.overflows && this.#separatorSettings[this.#currentSide].type === 'custom') {
                const chars = this.#separatorSettings[this.#currentSide].customChars;
                for (let i = 0; i < chars.length; i++) {
                    while (await layer.separatorOverflowsAt(i, RING_INNER, RING_OUTER)) {
                        const graphemes = SegmentGraphemes(chars[i]);
                        if (graphemes.length <= 1) {
                            break;
                        }
                        chars[i] = graphemes.slice(0, -1).join('');
                        this.#syncCustomInputsFromSettings();
                        this.#updateSeparators();
                        await new Promise(resolve => requestAnimationFrame(resolve));
                    }
                }
                didShrinkAny = true;
            }
        }

        if (didShrinkAny || didAdjustSpacing) {
            void this.#updateRingTextCutouts();
        }
    }

    async #applyFontSizePt(sizePt: number): Promise<void> {
        if (this.#fontSizeBtn) {
            this.#fontSizeBtn.textContent = String(sizePt);
        }
        // CoinTextLayer handles pt→px conversion internally
        await this.#applyToBothLayersAsync(layer => layer.setFont({ size: sizePt }));
        this.#updateSeparators();
        void this.#updateRingTextCutouts();
        await this.#runShrinkToFit();
    }

    #setupStyleToggles(): void {
        this.#boldBtn = document.getElementById('font-bold-btn') as HTMLButtonElement;
        this.#italicBtn = document.getElementById('font-italic-btn') as HTMLButtonElement;
        this.#underlineBtn = document.getElementById('font-underline-btn') as HTMLButtonElement;
        this.#capsBtn = document.getElementById('font-caps-btn') as HTMLButtonElement;

        if (this.#boldBtn) {
            this.#boldBtn.addEventListener('click', () => {
                const currentState = this.#fontStyles[this.#currentSide];
                currentState.bold = !currentState.bold;
                this.#updateStyleToggleUI();
                void this.#applyFontStyles();
            });
        }

        if (this.#italicBtn) {
            this.#italicBtn.addEventListener('click', () => {
                const currentState = this.#fontStyles[this.#currentSide];
                currentState.italic = !currentState.italic;
                this.#updateStyleToggleUI();
                void this.#applyFontStyles();
            });
        }

        if (this.#underlineBtn) {
            this.#underlineBtn.addEventListener('click', () => {
                const currentState = this.#fontStyles[this.#currentSide];
                currentState.underline = !currentState.underline;
                this.#updateStyleToggleUI();
                void this.#applyFontStyles();
            });
        }

        if (this.#capsBtn) {
            this.#capsBtn.addEventListener('click', () => {
                const currentState = this.#fontStyles[this.#currentSide];
                currentState.allCaps = !currentState.allCaps;
                this.#updateStyleToggleUI();
                void this.#applyFontStyles();
            });
        }
    }

    #updateStyleToggleUI(): void {
        const currentState = this.#fontStyles[this.#currentSide];
        const [topLayer] = this.#getTextLayers();
        const family = topLayer?.getFont()?.family ?? DEFAULT_FONT_FAMILY;

        if (this.#boldBtn) {
            this.#boldBtn.classList.toggle('active', currentState.bold);
            this.#boldBtn.setAttribute('aria-pressed', String(currentState.bold));
            this.#boldBtn.disabled = !FontHasFace(family, 'bold');
        }

        if (this.#italicBtn) {
            this.#italicBtn.classList.toggle('active', currentState.italic);
            this.#italicBtn.setAttribute('aria-pressed', String(currentState.italic));
            this.#italicBtn.disabled = !FontHasFace(family, 'italic');
        }

        if (this.#underlineBtn) {
            this.#underlineBtn.classList.toggle('active', currentState.underline);
            this.#underlineBtn.setAttribute('aria-pressed', String(currentState.underline));
        }

        if (this.#capsBtn) {
            this.#capsBtn.classList.toggle('active', currentState.allCaps);
            this.#capsBtn.setAttribute('aria-pressed', String(currentState.allCaps));
        }
    }

    #updateMetalColorUI(): void {
        const isMetalMode = this.#isMetalMode[this.#currentSide];
        const $metalColorBtn = document.getElementById('font-metal-color-btn');
        const textColorToggle = document.getElementById('text-color-type') as HTMLInputElement | null;
        const textColorPickerContainer = document.getElementById('text-color-picker-container');

        if ($metalColorBtn) {
            $metalColorBtn.classList.toggle('active', !isMetalMode);
            $metalColorBtn.setAttribute('aria-pressed', String(!isMetalMode));
        }

        if (textColorToggle) {
            textColorToggle.checked = !isMetalMode;
        }

        if (textColorPickerContainer) {
            textColorPickerContainer.classList.toggle('d-none', isMetalMode);
        }
    }

    async #applyFontStyles(): Promise<void> {
        const [topLayer] = this.#getTextLayers();
        const fontFamily = topLayer?.getFont()?.family ?? DEFAULT_FONT_FAMILY;
        await this.#applyFaceForSide(this.#currentSide, fontFamily);
        this.#updateStyleToggleUI();
        void this.#updateRingTextCutouts();
        void this.#runShrinkToFit();
    }

    #setupColorControls(): void {
        this.#wantsTextRingToggle = document.getElementById('wants-text-ring-switch') as HTMLInputElement;

        // Find all text control containers after the "wants text ring" toggle
        // These will be hidden when "no text ring" is selected
        const wantsRingContainer = this.#wantsTextRingToggle?.closest('.coin-text-question-container');
        if (wantsRingContainer) {
            let sibling = wantsRingContainer.nextElementSibling;
            while (sibling) {
                // Include all containers except the Save button
                if (sibling.classList.contains('coin-text-question-container') ||
                    sibling.id === 'top-text-input' || sibling.id === 'bottom-text-input' ||
                    sibling.querySelector('#top-text-input') || sibling.querySelector('#bottom-text-input')) {
                    this.#textControlContainers.push(sibling as HTMLElement);
                }
                sibling = sibling.nextElementSibling;
            }
        }

        // Text Ring color picker
        this.#textRingToggle = document.getElementById('text-ring-type') as HTMLInputElement;
        this.#textRingColorPicker = document.getElementById('text-ring-color-picker');
        this.#textRingColorSwatch = document.getElementById('text-ring-color-swatch') as HTMLInputElement;
        const textRingColorHexDisplay = document.getElementById('text-ring-color-hex-display');

        // Text Color picker (changes actual text color)
        const textColorToggle = document.getElementById('text-color-type') as HTMLInputElement;
        const textColorPickerContainer = document.getElementById('text-color-picker');
        this.#textColorPicker = document.getElementById('text-color-swatch') as HTMLInputElement;
        this.#textColorHexDisplay = document.getElementById('text-color-hex-display') as HTMLElement;

        // Helper to update Text Color toggle state based on ring settings
        // Metal text is only allowed when: ring is wanted AND ring is Color mode

        const savedMetalState: Record<'front' | 'back', boolean> = { front: false, back: false };

        const updateTextColorToggleState = () => {
            if (!textColorToggle) {
                return;
            }

            const side = this.#currentSide;
            const settings = this.#ringSettings[side];
            const allowMetalText = settings.wantsRing && settings.useColor;

            if (allowMetalText) {
                // Enable Metal option — restore saved state if it was metal before
                textColorToggle.disabled = false;
                if (savedMetalState[side]) {
                    textColorToggle.checked = false;
                    textColorToggle.dispatchEvent(new Event('change'));
                    savedMetalState[side] = false;
                }
            } else {
                // Disable Metal option - save current state, then force Color mode
                if (!textColorToggle.checked) {
                    savedMetalState[side] = true;
                }
                textColorToggle.disabled = true;
                if (!textColorToggle.checked) {
                    textColorToggle.checked = true;
                    textColorToggle.dispatchEvent(new Event('change'));
                }
            }
        };

        const updateCurrentRingVisibility = () => {
            const settings = this.#ringSettings[this.#currentSide];
            const ring = this.#currentRing;

            // Show ring only if user wants it AND has selected color mode
            if (settings.wantsRing && settings.useColor) {
                ring.show();
            } else {
                ring.hide();
            }

            this.#triggerPreviewUpdate();
        };

        // Helper to update text controls and layers visibility based on wantsRing setting
        // resizeImages: true when user toggles (scale images proportionally), false on init/side switch
        const updateTextControlsVisibility = (wantsRing: boolean, resizeImages: boolean = false) => {
            // Show/hide all text control containers
            for (const container of this.#textControlContainers) {
                container.classList.toggle('d-none', !wantsRing);
            }

            const [topLayer, bottomLayer] = this.#getTextLayers();
            if (topLayer) {
                topLayer.setVisible(wantsRing);
            }
            if (bottomLayer) {
                bottomLayer.setVisible(wantsRing);
            }

            this.#updateImageMaskForRingState(wantsRing, resizeImages);
        };

        if (this.#wantsTextRingToggle) {
            const wantsToggle = this.#wantsTextRingToggle;
            wantsToggle.addEventListener('change', () => {
                const wantsRing = wantsToggle.checked;

                this.#ringSettings[this.#currentSide].wantsRing = wantsRing;
                updateTextControlsVisibility(wantsRing, true);
                updateTextColorToggleState();
                updateCurrentRingVisibility();
                this.#currentEngine.emit('significantChange', { layerId: 'text-ring' });
            });

            // Set initial state from per-side settings (no resize on init)
            const wantsRing = this.#ringSettings[this.#currentSide].wantsRing;
            wantsToggle.checked = wantsRing;
            updateTextControlsVisibility(wantsRing, false);
            updateTextColorToggleState();
        }

        if (this.#textRingToggle && this.#textRingColorPicker) {
            const ringToggle = this.#textRingToggle;
            const colorPicker = this.#textRingColorPicker;

            ringToggle.addEventListener('change', () => {
                const useColor = ringToggle.checked;
                this.#ringSettings[this.#currentSide].useColor = useColor;

                colorPicker.classList.toggle('d-none', !useColor);

                // Update Text Color toggle state (Metal ring forces Color text)
                updateTextColorToggleState();

                updateCurrentRingVisibility();
                this.#currentEngine.emit('significantChange', { layerId: 'text-ring' });
            });

            // Initialize ring toggle from settings (not HTML default)
            const initialUseColor = this.#ringSettings[this.#currentSide].useColor;
            ringToggle.checked = initialUseColor;
            colorPicker.classList.toggle('d-none', !initialUseColor);

            if (this.#textRingColorSwatch && textRingColorHexDisplay) {
                const colorSwatch = this.#textRingColorSwatch;
                colorSwatch.addEventListener('input', () => {
                    const color = colorSwatch.value;
                    textRingColorHexDisplay.textContent = color;

                    this.#ringSettings[this.#currentSide].color = color;
                    this.#currentRing.setColor(color);
                    this.#triggerPreviewUpdate();
                    this.#currentEngine.emit('significantChange', { layerId: 'text-ring' });
                });

                const initialColor = this.#ringSettings[this.#currentSide].color;
                colorSwatch.value = initialColor;
                textRingColorHexDisplay.textContent = initialColor;
                this.#currentRing.setColor(initialColor);
            }
        }

        updateCurrentRingVisibility();

        const $separatorColorIndicator = document.getElementById('separator-color-indicator');

        if (textColorToggle && textColorPickerContainer) {
            textColorToggle.addEventListener('change', () => {
                const isColor = textColorToggle.checked;
                textColorPickerContainer.classList.toggle('d-none', !isColor);
                this.#isMetalMode[this.#currentSide] = !isColor;

                // Hide separator color picker in metal mode — separators inherit metal styling
                $separatorColorIndicator?.classList.toggle('d-none', !isColor);
                if (!isColor) {
                    this.#separatorSettings[this.#currentSide].color = null;
                    this.#applySeparatorColor();
                }

                if (isColor) {
                    this.#applyTextColors();
                } else {
                    this.#applyMetalText();
                }

                void this.#applyFontStyles();
            });

            // Set initial state from our defaults (not HTML toggle)
            const isMetalMode = this.#isMetalMode[this.#currentSide];
            textColorToggle.checked = !isMetalMode; // checked = color mode

            if (isMetalMode) {
                textColorPickerContainer.classList.add('d-none');
                $separatorColorIndicator?.classList.add('d-none');
                this.#applyMetalText();
            } else {
                this.#applyTextColors();
            }
        }

        if (this.#textColorPicker) {
            this.#textColorPicker.addEventListener('input', () => {
                const color = this.#textColorPicker?.value ?? '#000000';
                // Desktop picker applies to both layers
                this.#textColors[this.#currentSide].top = color;
                this.#textColors[this.#currentSide].bottom = color;
                this.#applyTextColors();

                // Update hex display
                if (this.#textColorHexDisplay) {
                    this.#textColorHexDisplay.textContent = color;
                }

                this.#syncMobileColorBars();
            });
        }
    }

    /** Apply per-layer text colors to both layers on the current side */
    #applyTextColors(): void {
        const colors = this.#textColors[this.#currentSide];
        const [top, bottom] = this.#getTextLayers();
        if (top) {
            top.setColor(colors.top);
        }
        if (bottom) {
            bottom.setColor(colors.bottom);
        }
    }

    /** Apply 'none' (metal/transparent) to both text layers */
    #applyMetalText(): void {
        this.#applyToBothLayers(layer => layer.setColor('none'));
    }

    /** Sync mobile color bar visuals to match #textColors for current side */
    #syncMobileColorBars(): void {
        const colors = this.#textColors[this.#currentSide];
        document.querySelectorAll<HTMLElement>('.text-color-indicator').forEach($indicator => {
            const $input = $indicator.querySelector<HTMLInputElement>('.text-color-input');
            const $colorBar = $indicator.querySelector<HTMLElement>('.color-bar');
            const target = $input?.dataset.target;
            const color = target === 'bottom' ? colors.bottom : colors.top;
            if ($input) {
                $input.value = color;
            }
            if ($colorBar) {
                $colorBar.style.backgroundColor = color;
            }
        });
    }

    #setupSideChangeListener(): void {
        // Listen for side selection changes
        const sideRadios = document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]');

        sideRadios.forEach(radio => {
            radio.addEventListener('change', () => {
                if (radio.checked) {
                    this.setSide(radio.value as 'front' | 'back');
                }
            });
        });
    }

    /**
     * Called when switching between front and back sides
     * Updates the input fields to reflect the current side's text values
     */
    setSide(side: 'front' | 'back'): void {
        this.#currentSide = side;
        this.#updateInputsForCurrentSide();
    }

    #updateInputsForCurrentSide(): void {
        const topLayer = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
        const bottomLayer = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;

        if (this.#topTextInput && topLayer) {
            this.#topTextInput.value = topLayer.getText();
        }

        if (this.#bottomTextInput && bottomLayer) {
            this.#bottomTextInput.value = bottomLayer.getText();
        }

        if (topLayer) {
            const font = topLayer.getFont();
            if (this.#fontFamilyBtn && font.family) {
                this.#fontFamilyBtn.textContent = font.family;
            }
            if (this.#fontSizeBtn && font.size) {
                // getFont() returns pt directly from CoinTextLayer
                this.#fontSizeBtn.textContent = String(font.size);
            }
        }

        this.#updateStyleToggleUI();

        const sideColors = this.#textColors[this.#currentSide];
        if (this.#textColorPicker) {
            this.#textColorPicker.value = sideColors.top;
        }
        if (this.#textColorHexDisplay) {
            this.#textColorHexDisplay.textContent = sideColors.top;
        }

        this.#syncMobileColorBars();
        this.#updateSeparatorUI();
        this.#updateRingUI();
        this.#updateMetalColorUI();

        // Refresh the ring's Konva image cache so previews sync correctly.
        // The cutouts are already rendered - we just need Konva to re-read the canvas.
        // This is lightweight (no async re-render) and emits layerUpdated for preview sync.
        if (this.#isMetalMode[this.#currentSide]) {
            this.#currentRing.refresh();
        }
    }

    /** Sync HTML inputs from the current separator settings */
    #syncCustomInputsFromSettings(): void {
        const chars = this.#separatorSettings[this.#currentSide].customChars;
        const singleInput = document.getElementById('custom-single') as HTMLInputElement | null;
        const leftInput = document.getElementById('custom-left') as HTMLInputElement | null;
        const rightInput = document.getElementById('custom-right') as HTMLInputElement | null;
        if (singleInput) {
            singleInput.value = chars[0] ?? '';
        }
        if (leftInput) {
            leftInput.value = chars[0] ?? '';
        }
        if (rightInput) {
            rightInput.value = chars[1] ?? chars[0] ?? '';
        }
    }

    #updateSeparatorUI(): void {
        const { type, customChars, color } = this.#separatorSettings[this.#currentSide];
        const separatorRadios = document.querySelectorAll<HTMLInputElement>('input[name="text-separator-radio"]');
        const customSingleInput = document.getElementById('custom-single') as HTMLInputElement | null;
        const customLeftInput = document.getElementById('custom-left') as HTMLInputElement | null;
        const customRightInput = document.getElementById('custom-right') as HTMLInputElement | null;

        separatorRadios.forEach(radio => {
            radio.checked = radio.value === type;
        });

        if (customSingleInput) {
            customSingleInput.value = customChars[0] ?? '';
        }
        if (customLeftInput) {
            customLeftInput.value = customChars[0] ?? '';
        }
        if (customRightInput) {
            customRightInput.value = customChars[1] ?? customChars[0] ?? '';
        }

        const $separatorColorIndicator = document.getElementById('separator-color-indicator');
        const separatorColorInput = document.getElementById('separator-color-input') as HTMLInputElement | null;
        const separatorColorSwatch = document.getElementById('separator-color-swatch') as HTMLElement | null;

        // Hide separator color picker in metal mode
        const isMetalMode = this.#isMetalMode[this.#currentSide];
        $separatorColorIndicator?.classList.toggle('d-none', isMetalMode);

        const displayColor = color ?? '#000000';
        if (separatorColorInput) {
            separatorColorInput.value = displayColor;
        }
        if (separatorColorSwatch) {
            separatorColorSwatch.style.backgroundColor = displayColor;
        }

        // Show/hide separator size controls based on whether a separator is active
        const sizeControls = document.getElementById('separator-size-controls');
        if (sizeControls) {
            sizeControls.classList.toggle('d-none', type === 'none');
            sizeControls.classList.toggle('d-flex', type !== 'none');
        }

        this.#updateCustomSeparatorMode?.();
    }

    #updateRingUI(): void {
        const settings = this.#ringSettings[this.#currentSide];

        if (this.#wantsTextRingToggle) {
            this.#wantsTextRingToggle.checked = settings.wantsRing;
        }

        for (const container of this.#textControlContainers) {
            container.classList.toggle('d-none', !settings.wantsRing);
        }

        const [topLayer, bottomLayer] = this.#getTextLayers();
        if (topLayer) {
            topLayer.setVisible(settings.wantsRing);
        }
        if (bottomLayer) {
            bottomLayer.setVisible(settings.wantsRing);
        }

        this.#updateImageMaskForRingState(settings.wantsRing);

        if (this.#textRingToggle) {
            this.#textRingToggle.checked = settings.useColor;
        }

        if (this.#textRingColorPicker) {
            this.#textRingColorPicker.classList.toggle('d-none', !settings.useColor);
        }

        if (this.#textRingColorSwatch) {
            this.#textRingColorSwatch.value = settings.color;
        }

        const hexDisplay = document.getElementById('text-ring-color-hex-display');
        if (hexDisplay) {
            hexDisplay.textContent = settings.color;
        }

        const ring = this.#currentRing;
        if (settings.wantsRing && settings.useColor) {
            ring.show();
        } else {
            ring.hide();
        }

        ring.setColor(settings.color);
        ring.setBlendMode(settings.translucent ? 'multiply' : 'source-over');

        // Sync translucent checkboxes
        document.querySelectorAll<HTMLInputElement>('#ring-translucent, #lg-ring-translucent').forEach($t => {
            $t.checked = settings.translucent;
        });
    }

    #triggerPreviewUpdate(): void {
        this.#currentEngine.emit('layerUpdated', { layerId: 'text-ring' });
    }

    #setupSeparatorControls(): void {
        const separatorRadios = document.querySelectorAll<HTMLInputElement>('input[name="text-separator-radio"]');
        const customSingleInput = document.getElementById('custom-single') as HTMLInputElement;
        const customLeftInput = document.getElementById('custom-left') as HTMLInputElement;
        const customRightInput = document.getElementById('custom-right') as HTMLInputElement;
        const customSplitWrapper = document.getElementById('custom-split-inputs') as HTMLElement;
        const customRadio = document.getElementById('custom-radio') as HTMLInputElement;

        separatorRadios.forEach(radio => {
            radio.addEventListener('change', () => {
                if (radio.checked) {
                    this.#separatorSettings[this.#currentSide].type = radio.value as SeparatorType;
                    this.#updateSeparators();
                    this.#updateSeparatorUI();
                    this.#currentEngine.emit('significantChange', { layerId: 'separator' });
                }
            });
        });

        const syncCustomChars = () => {
            const [topLayer, bottomLayer] = this.#getTextLayers();
            const hasBothText = (topLayer?.getText().length ?? 0) > 0 &&
                (bottomLayer?.getText().length ?? 0) > 0;

            if (hasBothText) {
                this.#separatorSettings[this.#currentSide].customChars = [customLeftInput.value, customRightInput.value];
            } else {
                this.#separatorSettings[this.#currentSide].customChars = [customSingleInput.value];
            }

            if (customRadio.checked) {
                this.#updateSeparators();
            }
            this.#currentEngine.emit('significantChange', { layerId: 'separator' });
        };

        // Toggle between single/split inputs based on whether both arches have text
        const customSingleLabel = document.getElementById('custom-single-label');

        this.#updateCustomSeparatorMode = () => {
            const [topLayer, bottomLayer] = this.#getTextLayers();
            const hasBothText = (topLayer?.getText().length ?? 0) > 0 &&
                (bottomLayer?.getText().length ?? 0) > 0;

            if (hasBothText) {
                customSingleLabel?.classList.add('d-none');
                customSplitWrapper.classList.remove('d-none');
                customSplitWrapper.classList.add('d-flex');
                // Sync single → split if switching modes
                if (!customLeftInput.value && customSingleInput.value) {
                    customLeftInput.value = customSingleInput.value;
                    customRightInput.value = customSingleInput.value;
                    syncCustomChars();
                    this.#debouncedShrinkToFit();
                }
            } else {
                customSingleLabel?.classList.remove('d-none');
                customSplitWrapper.classList.add('d-none');
                customSplitWrapper.classList.remove('d-flex');
                // Sync split → single if switching modes
                if (!customSingleInput.value && customLeftInput.value) {
                    customSingleInput.value = customLeftInput.value;
                }
                // Enforce 2-char limit for single mode
                const graphemes = SegmentGraphemes(customSingleInput.value);
                if (graphemes.length > 2) {
                    customSingleInput.value = graphemes.slice(0, 2).join('');
                }
                syncCustomChars();
            }
        };

        if (customSingleInput && customLeftInput && customRightInput && customRadio) {
            customSingleInput.addEventListener('focus', () => {
                customRadio.checked = true;
                this.#separatorSettings[this.#currentSide].type = 'custom';
                this.#updateSeparators();
                this.#updateSeparatorUI();
                this.#currentEngine.emit('significantChange', { layerId: 'separator' });
            });

            customSingleInput.addEventListener('input', () => {
                const graphemes = SegmentGraphemes(customSingleInput.value);
                if (graphemes.length > 2) {
                    customSingleInput.value = graphemes.slice(0, 2).join('');
                }
                syncCustomChars();
                this.#debouncedShrinkToFit();
            });

            for (const input of [customLeftInput, customRightInput]) {
                input.addEventListener('focus', () => {
                    customRadio.checked = true;
                    this.#separatorSettings[this.#currentSide].type = 'custom';
                    this.#updateSeparators();
                    this.#updateSeparatorUI();
                    this.#currentEngine.emit('significantChange', { layerId: 'separator' });
                });

                input.addEventListener('input', () => {
                    syncCustomChars();
                    this.#debouncedShrinkToFit();
                });
            }

            customRadio.addEventListener('change', () => {
                if (customRadio.checked) {
                    this.#separatorSettings[this.#currentSide].type = 'custom';
                    this.#updateSeparators();
                    this.#updateSeparatorUI();
                    this.#currentEngine.emit('significantChange', { layerId: 'separator' });
                }
            });
        }

        // Separator size controls
        const sizeControls = document.getElementById('separator-size-controls');
        const sizeDown = document.getElementById('separator-size-down');
        const sizeUp = document.getElementById('separator-size-up');

        if (sizeDown && sizeUp && sizeControls) {
            const changeSeparatorSize = (delta: number) => {
                const current = this.#separatorSettings[this.#currentSide].sizePt;
                const min = AVAILABLE_FONT_SIZES_PT[0];
                const sepType = this.#separatorSettings[this.#currentSide].type;
                const [topL, bottomL] = this.#getTextLayers();
                const hasBothText = (topL?.getText().length ?? 0) > 0 && (bottomL?.getText().length ?? 0) > 0;
                // Custom text separators in single mode: cap at max font size since no shrink runs
                // Everything else (dots, stars, split mode custom): allow larger, shrink handles overflow
                const max = sepType === 'custom' && !hasBothText ? AVAILABLE_FONT_SIZES_PT[AVAILABLE_FONT_SIZES_PT.length - 1] : 18;
                const next = Math.max(min, Math.min(max, current + delta));
                if (next === current) {
                    return;
                }
                this.#separatorSettings[this.#currentSide].sizePt = next;
                this.#applyToBothLayers(layer => layer.setSeparatorSize(next));
                void this.#updateRingTextCutouts();
                void this.#runShrinkToFit();
                this.#currentEngine.emit('significantChange', { layerId: 'separator' });
            };

            sizeDown.addEventListener('click', () => changeSeparatorSize(-1));
            sizeUp.addEventListener('click', () => changeSeparatorSize(1));
        }

        const separatorColorInput = document.getElementById('separator-color-input') as HTMLInputElement | null;
        const separatorColorSwatch = document.getElementById('separator-color-swatch') as HTMLElement | null;

        if (separatorColorInput && separatorColorSwatch) {
            separatorColorInput.addEventListener('input', () => {
                const color = separatorColorInput.value;
                this.#separatorSettings[this.#currentSide].color = color;
                separatorColorSwatch.style.backgroundColor = color;
                this.#applySeparatorColor();
                this.#currentEngine.emit('significantChange', { layerId: 'separator' });
            });

            // Stop color input click from bubbling to label
            separatorColorInput.addEventListener('click', e => e.stopPropagation());
        }
    }

    #applySeparatorColor(): void {
        const color = this.#separatorSettings[this.#currentSide].color;
        this.#applyToBothLayers(layer => layer.setSeparatorColor(color));
    }

    #applySeparatorSize(): void {
        const sizePt = this.#separatorSettings[this.#currentSide].sizePt;
        this.#applyToBothLayers(layer => layer.setSeparatorSize(sizePt));
    }

    #updateSeparators(): void {
        const [topLayer, bottomLayer] = this.#getTextLayers();
        if (!topLayer || !bottomLayer) {
            return;
        }

        const clearBoth = () => {
            topLayer.setSeparator('none', []);
            bottomLayer.setSeparator('none', []);
        };

        // Hide separators if text nearly wraps around
        if (topLayer.getTextLengthPercent() >= NEAR_WRAP_THRESHOLD ||
            bottomLayer.getTextLengthPercent() >= NEAR_WRAP_THRESHOLD) {
            clearBoth();
            void this.#updateRingTextCutouts();
            return;
        }

        const { type, customChars } = this.#separatorSettings[this.#currentSide];
        if (type === 'none') {
            clearBoth();
            void this.#updateRingTextCutouts();
            return;
        }

        const hasTop = topLayer.getText().length > 0;
        const hasBottom = bottomLayer.getText().length > 0;
        if (!hasTop && !hasBottom) {
            clearBoth();
            void this.#updateRingTextCutouts();
            return;
        }

        const positions = this.#calculateSeparatorPositions(topLayer, bottomLayer, hasTop, hasBottom);

        // Handle special single-text markers
        if (positions.length === 1 && positions[0] === USE_TOP_LAYER_CENTER) {
            topLayer.setSeparator(type, [50], customChars);
            bottomLayer.setSeparator('none', []);
        } else if (positions.length === 1 && positions[0] === USE_BOTTOM_LAYER_CENTER) {
            topLayer.setSeparator('none', []);
            bottomLayer.setSeparator(type, [50], customChars);
        } else {
            topLayer.setSeparator(positions.length > 0 ? type : 'none', positions, customChars);
            bottomLayer.setSeparator('none', []);
        }

        // Apply separator color and size (setSeparator recreates elements, so these must be reapplied)
        this.#applySeparatorColor();
        this.#applySeparatorSize();

        // Update ring cutouts to include separator cutouts (for Metal mode)
        void this.#updateRingTextCutouts();
    }

    #calculateSeparatorPositions(
        topLayer: CoinTextLayer,
        bottomLayer: CoinTextLayer,
        hasTopText: boolean,
        hasBottomText: boolean,
    ): number[] {
        // Text is centered at 50%, extends from (50 - half) to (50 + half)
        const topHalf = topLayer.getTextLengthPercent() / 2;
        const bottomHalf = bottomLayer.getTextLengthPercent() / 2;

        const topTextStart = 50 - topHalf;
        const topTextEnd = 50 + topHalf;
        const bottomTextStart = 50 - bottomHalf;
        const bottomTextEnd = 50 + bottomHalf;

        if (hasTopText && hasBottomText) {
            // Map bottom text edges to top path coordinates:
            // bottomPathPos → topPathPos = (150 - bottomPathPos) % 100
            const bottomStartOnTop = (150 - bottomTextStart) % 100;
            const bottomEndOnTop = (150 - bottomTextEnd) % 100;

            // Midpoint of gap between nearest text edges on each side
            const leftSepPos = (bottomStartOnTop + topTextStart) / 2;
            const rightSepPos = (topTextEnd + bottomEndOnTop) / 2;

            const minGap = OVERLAP_MARGIN;
            const leftFits = topTextStart - leftSepPos >= minGap &&
                leftSepPos - bottomStartOnTop >= minGap;
            const rightFits = rightSepPos - topTextEnd >= minGap &&
                bottomEndOnTop - rightSepPos >= minGap;

            if (!leftFits && !rightFits) {
                return [];
            }

            const positions: number[] = [];
            if (leftFits) {
                positions.push(leftSepPos);
            }
            if (rightFits) {
                positions.push(rightSepPos);
            }
            return positions;
        }

        // Single text: place separator opposite (right-side up on that layer's path)
        if (hasTopText) {
            return [USE_BOTTOM_LAYER_CENTER]; // 6 o'clock
        }
        if (hasBottomText) {
            return [USE_TOP_LAYER_CENTER]; // 12 o'clock
        }
        return [];
    }

    /**
     * Generate a circular mask as a blob URL.
     * The mask is black (visible) inside the circle, transparent outside.
     */
    #generateCircleMask(radius: number): string {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_SIZE;
        canvas.height = CANVAS_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return '';
        }

        ctx.fillStyle = 'black';
        ctx.beginPath();
        ctx.arc(CANVAS_CENTER, CANVAS_CENTER, radius, 0, Math.PI * 2);
        ctx.fill();

        return canvas.toDataURL('image/png');
    }

    /**
     * Update the image mask for the current side based on text ring state.
     * When wantsRing is false, the upload area extends to cover the ring space.
     * @param wantsRing Whether the text ring is enabled
     * @param resizeImages If true, intelligently resize images for the new area (use for toggle events)
     */
    #updateImageMaskForRingState(wantsRing: boolean, resizeImages: boolean = false): void {
        const newRadius = wantsRing ? MASK_RADIUS_WITH_RING : MASK_RADIUS_NO_RING;
        const maskUrl = this.#generateCircleMask(newRadius);
        const diameter = newRadius * 2;

        const bounds = {
            x: CANVAS_CENTER - newRadius,
            y: CANVAS_CENTER - newRadius,
            width: diameter,
            height: diameter,
        };

        this.#currentEngine.defineImageZone({
            id: 'coin-center',
            maskUrl,
            bounds,
        });

        const layers = this.#currentEngine.getLayers();
        const imageLayers: ImageLayer[] = [];
        for (const layer of layers.values()) {
            if (layer instanceof ImageLayer && !layer.isBackground) {
                imageLayers.push(layer);
            }
        }

        // Intelligently resize images when toggling (not on side switch or init)
        if (resizeImages && imageLayers.length > 0) {
            const oldRadius = wantsRing ? MASK_RADIUS_NO_RING : MASK_RADIUS_WITH_RING;
            const scaleFactor = newRadius / oldRadius;

            for (const layer of imageLayers) {
                const currentPos = layer.getPosition();
                const currentScale = layer.getScale();

                const offsetX = currentPos.x - CANVAS_CENTER;
                const offsetY = currentPos.y - CANVAS_CENTER;

                // Maintains the image's relative position within the circular area
                const newOffsetX = offsetX * scaleFactor;
                const newOffsetY = offsetY * scaleFactor;

                const newScaleX = currentScale.x * scaleFactor;
                const newScaleY = currentScale.y * scaleFactor;

                layer.setScale(newScaleX, newScaleY);
                layer.setPosition(CANVAS_CENTER + newOffsetX, CANVAS_CENTER + newOffsetY);
            }
        }

        for (const layer of imageLayers) {
            void layer.setMask(maskUrl);
        }
    }

    setTopTextColor(color: string): void {
        this.#textColors[this.#currentSide].top = color;
        if (!this.#isMetalMode[this.#currentSide]) {
            const topLayer = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
            if (topLayer) {
                topLayer.setColor(color);
            }
        }
    }

    setBottomTextColor(color: string): void {
        this.#textColors[this.#currentSide].bottom = color;
        if (!this.#isMetalMode[this.#currentSide]) {
            const bottomLayer = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;
            if (bottomLayer) {
                bottomLayer.setColor(color);
            }
        }
    }

    getTextColors(): { top: string; bottom: string } {
        return { ...this.#textColors[this.#currentSide] };
    }

    async setLayerFontSize(target: 'top' | 'bottom', sizePt: number): Promise<void> {
        const layerId = `${this.#currentSide}-${target}-text`;
        const layer = this.#currentEngine.getLayer(layerId) as CoinTextLayer | undefined;
        if (layer) {
            await layer.setFont({ size: sizePt });
            this.#updateSeparators();
            void this.#updateRingTextCutouts();
            void this.#runShrinkToFit();
        }
    }

    getLayerFontSizes(): { top: number; bottom: number } {
        const topLayer = this.#currentEngine.getLayer(`${this.#currentSide}-top-text`) as CoinTextLayer | undefined;
        const bottomLayer = this.#currentEngine.getLayer(`${this.#currentSide}-bottom-text`) as CoinTextLayer | undefined;
        return {
            top: topLayer?.getFont()?.size ?? 11,
            bottom: bottomLayer?.getFont()?.size ?? 11,
        };
    }

    setRingTranslucent(translucent: boolean): void {
        this.#ringSettings[this.#currentSide].translucent = translucent;
        this.#currentRing.setBlendMode(translucent ? 'multiply' : 'source-over');
        this.#currentEngine.emit('significantChange', { layerId: 'text-ring' });
    }

    isRingTranslucent(side: 'front' | 'back'): boolean {
        return this.#ringSettings[side].translucent;
    }

    getState(): TextState {
        const getSideState = (side: 'front' | 'back'): TextSideState => {
            const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
            const topLayer = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
            const bottomLayer = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;

            const topFont = topLayer?.getFont();
            const bottomFont = bottomLayer?.getFont();
            const font = topFont ?? bottomFont;

            return {
                topText: topLayer?.getText() ?? '',
                bottomText: bottomLayer?.getText() ?? '',
                fontFamily: font?.family ?? DEFAULT_FONT_FAMILY,
                topFontSize: topFont?.size ?? 11,
                bottomFontSize: bottomFont?.size ?? 11,
                topFontColor: this.#textColors[side].top,
                bottomFontColor: this.#textColors[side].bottom,
                fontStyles: { ...this.#fontStyles[side] },
                separator: { ...this.#separatorSettings[side] },
                ring: { ...this.#ringSettings[side] },
                isMetalMode: this.#isMetalMode[side],
            };
        };

        return {
            front: getSideState('front'),
            back: getSideState('back'),
        };
    }

    #getUnderlineSettings(fontFamily: string): {
        underlineOffsetRatio: number | undefined;
        maxUnderlineOffset: number | undefined;
        underlineWidthRatio: number | undefined;
    } {
        const fontLower = fontFamily.toLowerCase();
        const isOswald = fontLower.includes('oswald');
        const isRoboto = fontLower.includes('roboto');

        if (isOswald) {
            return {
                underlineOffsetRatio: OSWALD_UNDERLINE_OFFSET_RATIO,
                maxUnderlineOffset: OSWALD_MAX_UNDERLINE_OFFSET,
                underlineWidthRatio: OSWALD_UNDERLINE_WIDTH_RATIO,
            };
        } else if (isRoboto) {
            return {
                underlineOffsetRatio: undefined,
                maxUnderlineOffset: undefined,
                underlineWidthRatio: ROBOTO_UNDERLINE_WIDTH_RATIO,
            };
        }
        return {
            underlineOffsetRatio: undefined,
            maxUnderlineOffset: undefined,
            underlineWidthRatio: undefined,
        };
    }

    async restoreState(state: TextState): Promise<void> {
        const restoreSide = async (side: 'front' | 'back', sideState: TextSideState) => {
            const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
            const ring = side === 'front' ? this.#frontRing : this.#backRing;
            const topLayer = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
            const bottomLayer = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;

            this.#ringSettings[side] = { ...sideState.ring, translucent: sideState.ring.translucent ?? false };
            this.#isMetalMode[side] = sideState.isMetalMode;
            this.#fontStyles[side] = { ...sideState.fontStyles, allCaps: sideState.fontStyles.allCaps ?? false };
            const sep = sideState.separator;
            this.#separatorSettings[side] = { type: sep.type, customChars: sep.customChars ?? [], color: sep.color ?? null, sizePt: sep.sizePt ?? DEFAULT_FONT_SIZE_PT };

            const sepColor = this.#separatorSettings[side].color;
            const sepSize = this.#separatorSettings[side].sizePt;
            topLayer?.setSeparatorColor(sepColor);
            bottomLayer?.setSeparatorColor(sepColor);
            topLayer?.setSeparatorSize(sepSize);
            bottomLayer?.setSeparatorSize(sepSize);

            // Restore per-layer text colors (backwards compat: old saves have fontColor only)
            const topColor = sideState.topFontColor ?? sideState.fontColor ?? '#000000';
            const bottomColor = sideState.bottomFontColor ?? sideState.fontColor ?? '#000000';
            this.#textColors[side] = { top: topColor, bottom: bottomColor };

            // Apply text content first (before visibility is set)
            if (topLayer) {
                topLayer.setText(sideState.topText);
            }
            if (bottomLayer) {
                bottomLayer.setText(sideState.bottomText);
            }

            // Each side can have a different font; legacy saved names (Arial, Impact,
            // Comic Sans MS, ...) map onto the self-hosted catalog. Bump the face
            // sequence so an in-flight default/menu apply can't clobber the restore,
            // and never let a failed font fetch abort it — the design must come back
            // even if it renders by family name until the file loads.
            this.#faceSeq[side]++;
            let face: ResolvedFontFace | undefined;
            try {
                face = await ResolveFontFace(sideState.fontFamily, sideState.fontStyles.bold, sideState.fontStyles.italic);
            } catch (err) {
                console.error('failed to load font face for restored design', err);
            }
            const family = face?.family ?? ResolveFontFamily(sideState.fontFamily);
            if (face) {
                this.#fontStyles[side].bold = face.bold;
                this.#fontStyles[side].italic = face.italic;
            }

            const underlineSettings = this.#getUnderlineSettings(family);

            const textDecoration = sideState.fontStyles.underline ? 'underline' : 'none';
            const casing = this.#fontStyles[side].allCaps ? 'uppercase' as const : 'normal' as const;

            // Per-layer font sizes (backwards compat: old saves have single fontSize)
            const topFontSize = sideState.topFontSize ?? sideState.fontSize ?? 11;
            const bottomFontSize = sideState.bottomFontSize ?? sideState.fontSize ?? 11;

            const baseFontConfig = {
                family,
                weight: face?.weight ?? (sideState.fontStyles.bold ? 'bold' : 'normal'),
                style: face?.style ?? (sideState.fontStyles.italic ? 'italic' : 'normal'),
                textDecoration,
                fontUrl: face?.fontUrl,
                exactFontFace: face ? true : undefined,
                casing,
                ...underlineSettings,
            };

            if (sideState.isMetalMode) {
                await Promise.all([
                    topLayer?.setFont({ ...baseFontConfig, size: topFontSize, color: 'none' }),
                    bottomLayer?.setFont({ ...baseFontConfig, size: bottomFontSize, color: 'none' }),
                ]);
            } else {
                await Promise.all([
                    topLayer?.setFont({ ...baseFontConfig, size: topFontSize, color: topColor }),
                    bottomLayer?.setFont({ ...baseFontConfig, size: bottomFontSize, color: bottomColor }),
                ]);
            }

            const wantsRing = sideState.ring.wantsRing;
            if (wantsRing && sideState.ring.useColor) {
                ring.setColor(sideState.ring.color);
                ring.show();
                ring.setBlendMode(sideState.ring.translucent ? 'multiply' : 'source-over');
            } else {
                ring.hide();
            }

            // Set text layer visibility based on wantsRing (AFTER text/font to ensure visibility sticks)
            if (topLayer) {
                topLayer.setVisible(wantsRing);
            }
            if (bottomLayer) {
                bottomLayer.setVisible(wantsRing);
            }

            const radius = wantsRing ? MASK_RADIUS_WITH_RING : MASK_RADIUS_NO_RING;
            const maskUrl = this.#generateCircleMask(radius);
            const diameter = radius * 2;
            engine.defineImageZone({
                id: 'coin-center',
                maskUrl,
                bounds: {
                    x: CANVAS_CENTER - radius,
                    y: CANVAS_CENTER - radius,
                    width: diameter,
                    height: diameter,
                },
            });
        };

        // Restore both sides (await to ensure fonts are loaded before continuing)
        await Promise.all([
            restoreSide('front', state.front),
            restoreSide('back', state.back),
        ]);

        // Shrink-to-fit for both sides (different fonts/text may need adjustment)
        // Run these sequentially and await them to ensure font sizes are finalized
        const originalSideForScale = this.#currentSide;
        this.#currentSide = 'front';
        await this.#runShrinkToFit();
        this.#currentSide = 'back';
        await this.#runShrinkToFit();
        this.#currentSide = originalSideForScale;

        // Update font dropdowns from the layer's actual state (after shrink-to-fit)
        // This ensures the UI reflects the final font size, not just the saved value
        this.#updateInputsForCurrentSide();

        this.#updateRingUI();
        this.#updateStyleToggleUI();
        this.#updateMetalColorUI();

        this.#updateBothSidesCutoutsAndSeparators();

        // Trigger preview updates for both sides so selector previews reflect restored state
        this.#frontEngine.emit('layerUpdated', {});
        this.#backEngine.emit('layerUpdated', {});
    }
}
